import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InteractionFeed } from './InteractionFeed';
import { AddMessageModal } from './AddMessageModal';
import { HubCommunicationCard } from './HubCommunicationCard';
import { HubParticipantPill } from './HubParticipantPill';
import { useHubStore, HUB_CLIENT_THREAD_KEY } from '../../stores/hubStore';
import { useProfileStore } from '../../stores/profileStore';
import { HubContext } from '../../types/hub';
import { Quote } from '../../types/quote';
import MyQuoteCard from '../quotes/MyQuoteCard';
import UpsertQuoteModal from '../quotes/UpsertQuoteModal';
import { useQuoteStore } from '../../stores/quoteStore';
import { useHubInteractionsByParticipant } from '../../hooks/useHubInteractionsByParticipant';
import { useHubUnreadPolling } from '../../hooks/useHubUnreadPolling';

type SelectableParticipant = {
  id: string;
  first_name: string;
  avatar_url?: string | null;
  label?: string;
  focusQuoteId: string | null;
};

interface FreelancerHubViewProps {
  context: HubContext;
  quote?: Quote | null;
  quoteId?: string | null;
  selectedQuoteId?: string | null;
  onSelectedQuoteIdChange?: (quoteId: string | null) => void;
  readOnly?: boolean;
  /** Admin hub: RPC “view as” profile (principal freelancer). */
  adminEffectiveViewerId?: string | null;
}

export const FreelancerHubView: React.FC<FreelancerHubViewProps> = ({
  context,
  quote,
  quoteId,
  selectedQuoteId,
  onSelectedQuoteIdChange,
  readOnly = false,
  adminEffectiveViewerId = null,
}) => {
  const { t } = useTranslation(['hub', 'quotes', 'common', 'notifications']);
  const { profile } = useProfileStore();
  const { hubUnreadCountByParticipantKey, fetchHubUnreadCounts, fetchHubContext } = useHubStore();
  const { quotes, fetchQuoteById } = useQuoteStore();
  const [showAddMessage, setShowAddMessage] = useState(false);
  const [selectedParticipantId, setSelectedParticipantId] = useState<string | null>(null);
  const [editingQuoteId, setEditingQuoteId] = useState<string | null>(null);

  const displayQuote =
    quoteId != null
      ? (quotes.find(q => String(q.id) === String(quoteId)) ?? null)
      : (quote ?? null);

  const quoteForModal =
    editingQuoteId != null
      ? (quotes.find(q => String(q.id) === String(editingQuoteId)) ?? null)
      : null;

  /** Alliance member: only talks to the principal (quote owner). No client, no other alliances. */
  const isAllianceMember =
    context.view_type === 'alliance' &&
    displayQuote != null &&
    profile?.id != null &&
    profile.id !== displayQuote.quote_owner?.id;

  /** Alliance members see their child quote in "Mon offre", not the redacted principal submission. */
  const quoteForMyOfferCard = useMemo((): Quote | null => {
    if (!displayQuote) return null;
    if (!isAllianceMember) return displayQuote;

    const myAlliance = displayQuote.alliances?.find(
      a => a.invited_freelancer.profile_id === profile?.id
    );
    const childQuote = myAlliance?.child_quote;
    if (!childQuote) return displayQuote;

    return {
      ...displayQuote,
      id: childQuote.id,
      status: childQuote.status,
      submitted_at: childQuote.submitted_at,
      tasks: childQuote.tasks,
      alliances: [],
      quote_owner: childQuote.quote_owner,
      parent_quote_id: displayQuote.id,
      parent_quote_status: displayQuote.status,
      parent_quote_owner: displayQuote.quote_owner,
      message: null,
      change_request_message: null,
    };
  }, [displayQuote, isAllianceMember, profile?.id]);

  const selectableParticipants: SelectableParticipant[] = useMemo(() => {
    const principalOwner = displayQuote?.parent_quote_owner ?? displayQuote?.quote_owner;
    if (isAllianceMember && principalOwner) {
      const p = principalOwner;
      return [
        {
          id: p.id,
          first_name: p.first_name,
          avatar_url: p.avatar_url ?? null,
          label: [p.first_name, p.last_name].filter(Boolean).join(' ').trim() || p.first_name,
          focusQuoteId: displayQuote.id,
        },
      ];
    }

    const client = context.job.client;
    const participants: SelectableParticipant[] = [
      {
        id: client.id,
        first_name: client.first_name,
        avatar_url: client.avatar_url ?? null,
        label: client.first_name,
        focusQuoteId: null,
      },
    ];

    if (!displayQuote) {
      return participants;
    }

    const hasParent = !!displayQuote.parent_quote_id;
    const parentPrincipalOwner = hasParent ? displayQuote.parent_quote_owner : null;
    if (parentPrincipalOwner) {
      participants.push({
        id: parentPrincipalOwner.id,
        first_name: parentPrincipalOwner.first_name,
        avatar_url: parentPrincipalOwner.avatar_url ?? null,
        focusQuoteId: displayQuote.id,
      });
    }

    const allianceMembers = (displayQuote.alliances ?? []).map(a => ({
      freelancer: a.invited_freelancer,
      allianceQuoteId: (a as { id?: string }).id ?? null,
    }));
    for (const { freelancer, allianceQuoteId } of allianceMembers) {
      participants.push({
        id: freelancer.profile_id,
        first_name: freelancer.first_name,
        avatar_url: freelancer.avatar_url ?? null,
        focusQuoteId: allianceQuoteId,
      });
    }

    return participants;
  }, [context.job.client, displayQuote, isAllianceMember]);

  useEffect(() => {
    fetchHubUnreadCounts(context.job.id, { adminEffectiveViewerId });
  }, [context.job.id, fetchHubUnreadCounts, adminEffectiveViewerId]);

  const participantDescriptors = useMemo(
    () =>
      selectableParticipants.map(participant => ({
        participantKey: participant.focusQuoteId ?? HUB_CLIENT_THREAD_KEY,
        quoteId: participant.focusQuoteId,
        participantId: participant.id,
      })),
    [selectableParticipants]
  );

  const selectedParticipant =
    selectableParticipants.find(p => p.id === selectedParticipantId) ?? null;

  const currentParticipantKey =
    selectedParticipant != null
      ? (selectedParticipant.focusQuoteId ?? HUB_CLIENT_THREAD_KEY)
      : (participantDescriptors[0]?.participantKey ?? HUB_CLIENT_THREAD_KEY);

  const {
    getInteractions,
    getLoading,
    getLoadingMore,
    getHasMore,
    loadMore,
    refresh,
    refreshParticipantNow,
  } = useHubInteractionsByParticipant(
    context.job.id,
    participantDescriptors,
    currentParticipantKey,
    adminEffectiveViewerId
  );

  useEffect(() => {
    if (selectableParticipants.length === 0) {
      setSelectedParticipantId(null);
      return;
    }
    if (isAllianceMember) {
      setSelectedParticipantId(selectableParticipants[0]?.id ?? null);
      return;
    }
    if (selectedQuoteId != null && selectedQuoteId !== '') {
      const matched = selectableParticipants.find(
        p => p.focusQuoteId != null && String(p.focusQuoteId) === String(selectedQuoteId)
      );
      if (matched) {
        setSelectedParticipantId(matched.id);
        return;
      }
    }
    setSelectedParticipantId(prev => {
      if (prev && selectableParticipants.some(p => p.id === prev)) return prev;
      return selectableParticipants[0]?.id ?? null;
    });
  }, [selectableParticipants, isAllianceMember, selectedQuoteId]);

  const interactions = getInteractions(currentParticipantKey);
  const loading = getLoading(currentParticipantKey);
  const loadingMore = getLoadingMore(currentParticipantKey);
  const hasMore = getHasMore(currentParticipantKey);
  const loadMoreForCurrent = loadMore(currentParticipantKey);
  const refreshCurrent = refresh(currentParticipantKey);

  useHubUnreadPolling(
    readOnly ? undefined : context.job.id,
    readOnly ? 0 : 60_000,
    refreshParticipantNow
  );

  const recipientIds = selectedParticipantId ? [selectedParticipantId] : [];

  const isTalkingToClient = !isAllianceMember && selectedParticipant?.id === context.job.client.id;

  const hasAcceptedQuoteRequestForCurrentFreelancer =
    (profile?.id &&
      context.job.quote_requests?.some(
        request => request.freelancer.id === profile.id && request.status === 'accepted'
      )) ??
    false;

  const hasSubmittedQuoteForCurrentFreelancer =
    !!profile?.id &&
    !!displayQuote &&
    displayQuote.quote_owner.id === profile.id &&
    displayQuote.submitted_at != null;

  const canSendMessage =
    !readOnly &&
    recipientIds.length > 0 &&
    (!isTalkingToClient ||
      hasAcceptedQuoteRequestForCurrentFreelancer ||
      hasSubmittedQuoteForCurrentFreelancer);

  const handleMessageSent = () => {
    refreshCurrent();
    fetchHubUnreadCounts(context.job.id, { adminEffectiveViewerId });
  };

  if (!profile?.id) return null;

  const handleSelectParticipant = (participant: SelectableParticipant) => {
    setSelectedParticipantId(participant.id);
    onSelectedQuoteIdChange?.(participant.focusQuoteId);
  };

  const header =
    selectableParticipants.length > 0 ? (
      <div className="flex w-full items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          {selectableParticipants[0] && (
            <HubParticipantPill
              key={selectableParticipants[0].id}
              id={selectableParticipants[0].id}
              first_name={selectableParticipants[0].first_name}
              avatar_url={selectableParticipants[0].avatar_url}
              label={selectableParticipants[0].label}
              isActive={selectableParticipants[0].id === selectedParticipantId}
              onClick={() => handleSelectParticipant(selectableParticipants[0])}
              unreadCount={
                hubUnreadCountByParticipantKey[
                  selectableParticipants[0].focusQuoteId ?? HUB_CLIENT_THREAD_KEY
                ] ?? 0
              }
            />
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-4">
          {selectableParticipants.slice(1).map(participant => (
            <HubParticipantPill
              key={participant.id}
              id={participant.id}
              first_name={participant.first_name}
              avatar_url={participant.avatar_url}
              label={participant.label}
              isActive={participant.id === selectedParticipantId}
              onClick={() => handleSelectParticipant(participant)}
              unreadCount={
                hubUnreadCountByParticipantKey[participant.focusQuoteId ?? HUB_CLIENT_THREAD_KEY] ??
                0
              }
            />
          ))}
        </div>
      </div>
    ) : undefined;

  return (
    <>
      {quoteForMyOfferCard && (
        <div className="mb-10">
          <div className="mb-2 text-dark text-xs uppercase font-semibold">
            {t('my_offer', { ns: 'quotes', defaultValue: 'Mon offre' })}
          </div>
          <MyQuoteCard
            quote={quoteForMyOfferCard}
            readOnly={readOnly}
            onEdit={q => setEditingQuoteId(q.id)}
          />
        </div>
      )}
      <HubCommunicationCard
        sectionTitle={t('interaction_history', { ns: 'hub' })}
        header={header}
        addMessageButton={
          canSendMessage
            ? {
                onClick: () => setShowAddMessage(true),
                disabled: false,
                label: t('addMessage'),
              }
            : undefined
        }
        addMessageModal={
          canSendMessage && showAddMessage ? (
            <AddMessageModal
              jobId={context.job.id}
              quoteId={quoteId ?? undefined}
              recipientIds={recipientIds}
              onClose={() => setShowAddMessage(false)}
              onSent={handleMessageSent}
            />
          ) : undefined
        }
      >
        <InteractionFeed
          key={selectedParticipantId ?? quoteId ?? 'feed'}
          interactions={interactions}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={hasMore}
          onLoadMore={loadMoreForCurrent}
          currentUserId={profile.id}
          context={context}
        />
      </HubCommunicationCard>
      {!readOnly && editingQuoteId && quoteForModal && (
        <UpsertQuoteModal
          quote={quoteForModal}
          job={quoteForModal.job}
          onClose={() => {
            const id = editingQuoteId;
            setEditingQuoteId(null);
            fetchHubContext(context.job.id);
            if (id) fetchQuoteById(id);
          }}
        />
      )}
    </>
  );
};
